import React, { useMemo } from 'react';
import { useModal } from 'components/modals/ModalContext';
import { TimelineItemType } from 'types/TimelineTypes';
import { formatTimelineDate } from 'services/utils/dateTimeUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { EmailReply } from '../../views/email/EmailTypes';
import { faReply } from '@fortawesome/pro-light-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import EmailReplyViewer from '../../views/email/EmailReplyViewer';
import '../../style/scss/tooltip.scss';

interface TimelineEmailReplyProps {
    placement: 'summary' | 'page';
    item: TimelineItemType;
    reply: EmailReply;
    timeline_page: string;
    timeline_page_id: number;
}

const TimelineEmailReply: React.FC<TimelineEmailReplyProps> = ({
    placement, item, reply
}) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();

    // Format the received date
    const replyDate = formatTimelineDate(item.ordering_date, false);

    // Get the reply title
    const title = reply.subject || t('timeline.email_reply.general.reply_fallback_title');

    // Generate the plain body
    const plainBody = useMemo(() => {
        const html = reply.body || '';
        const element = document.createElement('div');

        element.innerHTML = html
            .replace(/<\/p>/gi, '</p> ')
            .replace(/<br\s*\/?>/gi, ' ')
            .replace(/<\/div>/gi, '</div> ')
            .replace(/<\/li>/gi, '</li> ');

        return (element.textContent || element.innerText || '').replace(/\s+/g, ' ').trim();
    }, [reply.body]);

    // Open the reply modal
    const handleOpenReplyViewer = () => {
        initializeModal(
            <EmailReplyViewer
                reply={reply}
                mode="modal"
                receivedDate={replyDate}
            />,
            {
                modalSize: 'medium',
                title,
            }
        );
    };

    return (
        <div className='timeline-item email-reply'>
            {placement === 'page' && (
                <>
                    <div className='marker-container'>
                        <div className="big-marker">
                            <FontAwesomeIcon icon={faReply as IconProp} className='note-icon fa-fw' />
                        </div>
                    </div>
                    <div className='email-content' onClick={handleOpenReplyViewer} style={{ cursor: 'pointer' }}>
                        <div className='email-header'>
                            <div className='email-subject'>
                                {t('timeline.email_reply.general.reply_from_label', { from: reply.from_name || reply.from_email })}
                            </div>
                            <div className='email-message'>
                                <div className='left-items'>
                                    <p className="page-email-body-preview">
                                        {plainBody}
                                    </p>
                                </div>
                            </div>
                            <div className='email-info'>
                                <div className='left-items'>
                                    <span>{replyDate}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
            {placement === 'summary' && (
                <div className='email-content'>
                    <div className='marker-container'>
                        <div className="medium-marker email">
                            <FontAwesomeIcon icon={faReply as IconProp} className='note-icon fa-fw' />
                        </div>
                    </div>
                    <div>
                        <div
                            className='email-subject'
                            onClick={handleOpenReplyViewer}
                            style={{ cursor: 'pointer' }}
                        >
                            {title}
                        </div>
                        <div className='email-info'>
                            <div className='left-items'>
                                <span>{replyDate}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimelineEmailReply;
